import React, { useRef, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Box, Container, Typography, CircularProgress } from "@mui/material";
import { ChevronDown } from 'lucide-react';
import { ToastContainer, toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import 'react-toastify/dist/ReactToastify.css';
import Carousel from "../Components/Carousel/Carousel.js";
import ProjectModal from "../Components/Project/ProjectModal";
import ProjectGallery from "../Components/Common/projectGallery";
import HeaderPattern from "../Components/Common/HeaderPattern";
import { api } from "../config";
const redGradient = "linear-gradient(90deg, #e3764c 0%, #99191b 100%)";

export default function FinanzaDiProgetto() {
    const pageRef = useRef(null);
    const { t } = useTranslation();
    const [allProjects, setAllProjects] = useState([]);
    const [selectedProjectIds, setSelectedProjectIds] = useState([]);
    const [carouselProjects, setCarouselProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedProject, setSelectedProject] = useState(null);

    const fetchPPPProjects = async () => {
        try {
            setLoading(true);
            const response = await fetch(api("/getFinanzaDiProgetto.php"));
            const data = await response.json();

            if (!data.success || !Array.isArray(data.allProjects)) {
                console.error("Errore API:", data.message || data.error);
                toast.error(t('projectFinance.errors.loadFailed'));
                return;
            }

            const normalized = data.allProjects.map((p) => ({
                ...p,
                id: p.id ?? p.overlayText ?? "",
                titolo: p.titolo ?? p.overlayText ?? "",
                descrizione: p.descrizione ?? p.description ?? "",
                citta: p.citta ?? p.city ?? "",
                inizio: p.inizio || "",
                fine: p.fine || "",
                categorie: p.categorie || [],
                categorie_nomi: p.categorie_nomi || [],
                categoria_id: p.categoria_id || p.category_id || "",
                category: p.category || "",
                is_important: p.is_important || p.isImportant || 0,
                isImportant: Boolean(p.is_important || p.isImportant),
                images: Array.isArray(p.images) ? p.images : [],
                heroImage: p.heroImage ?? null
            }));

            setAllProjects(normalized);
            setSelectedProjectIds(data.selectedProjectIds || []);
            setCarouselProjects(normalized.slice(0, 5));

        } catch (e) {
            console.error("Errore caricamento progetti PPP:", e);
            toast.error(t('projectFinance.errors.connectionFailed'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPPPProjects();
    }, []);

    const getSelectedProjects = () => {
        if (selectedProjectIds.length === 0) {
            return allProjects;
        }
        const selected = selectedProjectIds.map(id =>
            allProjects.find(project => project.id === id)
        ).filter(project => project != null);
        return selected;
    };

    return (
        <div ref={pageRef}>
            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />

            {/* HERO SECTION – fills the viewport like ChiSiamo */}
            <Box
                id="finanza-hero"
                sx={{
                    width: "100%",
                    height: "calc(100vh - 95px)",
                    minHeight: 500,
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                {/* Spacer top */}
                <Box sx={{ height: { xs: 40, sm: 80, md: 150 } }} />

                {/* HEADER */}
                <Box
                    className="esi-transition-header"
                    sx={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        borderTop: "2px solid #fff",
                        borderBottom: "2px solid #fff",
                        background: redGradient,
                        overflow: "hidden",
                        py: "1.2rem",
                    }}
                >
                    <Box
                        sx={{
                            flex: { xs: "0 0 35%", sm: "0 0 45%", md: "0 0 50%" },
                            display: { xs: "none", sm: "flex" },
                            alignItems: "center",
                            justifyContent: "flex-start",
                            height: "100%",
                        }}
                    >
                        <HeaderPattern
                            style={{
                                width: "100%",
                                height: "300px",
                                maxHeight: "300px",
                                overflow: "visible",
                                transform: "scale(1.3) translateX(-10%)",
                                transformOrigin: "left center",
                                userSelect: "none",
                                pointerEvents: "none",
                            }}
                        />
                    </Box>

                    <Box
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: { xs: "center", sm: "flex-end" },
                            justifyContent: "center",
                            paddingRight: { xs: "16px", sm: "calc(max(24px, (100vw - 1400px) / 2 + 24px))" },
                            pl: { xs: 2, sm: 0 },
                            gap: 0.5,
                        }}
                    >
                        <Typography
                            variant="h1"
                            sx={{
                                fontFamily: "'Poppins', sans-serif",
                                fontWeight: 600,
                                fontSize: { xs: "1.5rem", sm: "2rem", md: "clamp(1.8rem, 2.5vw, 2.5rem)" },
                                color: "#fff",
                                lineHeight: 1.1,
                                letterSpacing: 0.5,
                                textAlign: { xs: "center", sm: "right" },
                                whiteSpace: { sm: "nowrap" },
                                m: 0,
                            }}
                        >
                            {t('projectFinance.title')}
                        </Typography>

                        <Typography
                            sx={{
                                maxWidth: 640,
                                fontFamily: "'Poppins', sans-serif",
                                fontWeight: 300,
                                fontSize: { xs: "0.7rem", sm: "0.85rem", md: "0.90rem" },
                                color: "#fff",
                                opacity: 0.85,
                                lineHeight: 1.4,
                                letterSpacing: 0.3,
                                textAlign: { xs: "center", sm: "right" },
                            }}
                        >
                            {t('projectFinance.subtitle')}
                        </Typography>
                    </Box>
                </Box>

                {/* INTRO TEXT */}
                <Container
                    sx={{
                        mt: 8,
                        maxWidth: "1400px !important",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: { xs: "stretch", sm: "flex-start" },
                            justifyContent: "space-between",
                            gap: 2,
                            mb: 0,
                            flexDirection: { xs: "column", sm: "row" },
                        }}
                    >
                        <Typography
                            sx={{
                                fontFamily: "'Roboto', sans-serif", fontWeight: 300,
                                lineHeight: 1.5,
                                textAlign: "justify",
                                flex: 1,
                            }}
                        >
                            {t('projectFinance.intro')}
                        </Typography>
                    </Box>
                </Container>

                {/* Spacer – pushes arrow towards the bottom */}
                <Box sx={{ flex: 1 }} />

                {/* DOWN ARROW */}
                <Box
                    onClick={() => {
                        const hero = document.getElementById('finanza-hero');
                        if (hero && hero.nextElementSibling) {
                            hero.nextElementSibling.scrollIntoView({ behavior: 'smooth' });
                        }
                    }}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pt: '16px',
                        pb: '40px',
                        flexShrink: 0,
                        cursor: 'pointer',
                    }}
                >
                    <motion.div
                        animate={{ y: [0, 10, 0] }}
                        transition={{
                            duration: 1.5,
                            repeat: Infinity,
                            ease: "easeInOut",
                        }}
                    >
                        <ChevronDown size={36} color="rgb(152,25,20)" strokeWidth={2} />
                    </motion.div>
                </Box>
            </Box>

            {/* CAROUSEL – same width as text container */}
            <Container sx={{ maxWidth: "1400px !important", px: { xs: 2, sm: 3 } }}>
                <Box
                    sx={{
                        width: "100%",
                        aspectRatio: "16 / 9",
                        overflow: "hidden",
                        borderRadius: "4px",
                    }}
                >
                    {loading ? (
                        <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#111" }}>
                            <CircularProgress color="error" />
                        </Box>
                    ) : (
                        <Carousel projects={carouselProjects} />
                    )}
                </Box>
            </Container>

            {/* TESTI ALLINEATI */}
            <Container
                sx={{
                    mt: 0,
                    maxWidth: "1400px !important",
                    pb: 2,
                }}
            >

                <Box
                    sx={{
                        mt: 4,
                        width: "100%",
                    }}
                >
                    <Box sx={{ display: "grid", gap: 4 }}>
                        <Box>
                            <h5 style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 400, color: "#99191b", marginTop: 0, marginBottom: 8, textAlign: "left" }}>
                                {t('projectFinance.section1.title')}
                            </h5>
                            <Typography sx={{ color: "#333", lineHeight: 1.5, fontFamily: "'Roboto', sans-serif", fontWeight: 300, textAlign: "justify" }}>
                                {t('projectFinance.section1.text')}
                            </Typography>
                        </Box>

                        <Box>
                            <h5 style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 400, color: "#99191b", marginTop: 0, marginBottom: 8, textAlign: "left" }}>
                                {t('projectFinance.section2.title')}
                            </h5>
                            <Typography sx={{ color: "#333", lineHeight: 1.5, fontFamily: "'Roboto', sans-serif", fontWeight: 300, textAlign: "justify" }}>
                                {t('projectFinance.section2.text')}
                            </Typography>
                        </Box>

                        <Box>
                            <h5 style={{ fontFamily: "'Roboto', sans-serif", fontWeight: 400, color: "#99191b", marginTop: 0, marginBottom: 8, textAlign: "left" }}>
                                {t('projectFinance.section3.title')}
                            </h5>
                            <Typography sx={{ color: "#333", lineHeight: 1.5, fontFamily: "'Roboto', sans-serif", fontWeight: 300, textAlign: "justify" }}>
                                {t('projectFinance.section3.text')}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Container>

            {/* GALLERIA PROGETTI */}
            <Container sx={{ mt: 6, maxWidth: "1400px !important" }}>
                {loading ? (
                    <Box sx={{ py: 8, display: "flex", justifyContent: "center" }}>
                        <CircularProgress color="error" />
                    </Box>
                ) : (
                    <ProjectGallery
                        projects={getSelectedProjects()}
                        onSelect={setSelectedProject}
                    />
                )}
            </Container>

            {/* TESTO FINALE */}
            <Container sx={{ mt: 6, maxWidth: "1400px !important", pb: 4 }}>
                <Box sx={{ display: "grid", gap: 4 }}>
                    <Box>
                        <Typography sx={{ color: "#333", lineHeight: 1.5, fontFamily: "'Roboto', sans-serif", fontWeight: 300, textAlign: "justify" }}>
                            {t('projectFinance.closingText')}
                        </Typography>
                    </Box>
                </Box>
            </Container>

            {/* MODAL PROGETTO */}
            <AnimatePresence>
                {selectedProject && (
                    <ProjectModal
                        project={selectedProject}
                        isOpen={!!selectedProject}
                        onClose={() => setSelectedProject(null)}
                    />
                )}
            </AnimatePresence>

        </div>
    );
}
